import { hasRole, useAuthOptional } from '../lib/auth';
import { ActionButton, ErrorBox, Section, useApi } from './kit';

// Buying signals ("why now", src/research/signals.ts): public events that make outreach timely, each with the quote
// Outlevo read on its page, the page and the date. Shown on the account, on the campaign (its accounts with a fresh
// one, and the opener their first email gets) and on Readiness Cards (packages/components).

export type Signal = {
  id: string; type: 'hiring' | 'funding' | 'new_leader' | 'launch' | 'tech_change'; headline: string; quote: string; source_url: string;
  published_at: string | null; observed_at: string; fresh_until: string | null; fresh: boolean; confidence: number; relevance: string | null;
};
type CampaignSignals = {
  accounts: number; with_signal: number;
  data: Array<{ company_id: string; company: string; domain: string; why_now: string; signals: Signal[] }>;
  rechecks: { on: boolean; every_days: number; credits_each: number | null };
};

export const SIGNAL_LABEL: Record<Signal['type'], string> = {
  hiring: 'Hiring', funding: 'Funding', new_leader: 'New leader', launch: 'Launch or expansion', tech_change: 'Tech change',
};

const day = (iso: string) => new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
const where = (url: string) => { try { const u = new URL(url); return `${u.hostname.replace(/^www\./, '')}${u.pathname === '/' ? '' : u.pathname.replace(/\/$/, '')}`; } catch { return url; } };

/** One signal: what happened, the words on the page, the page and its date. */
function SignalItem({ s }: { s: Signal }) {
  const dated = s.published_at ? day(s.published_at) : `listed ${day(s.observed_at)}`;
  return (
    <li className={`sig${s.fresh ? '' : ' sig-old'}`}>
      <div><span className={`chip${s.fresh ? ' done' : ''}`}>{SIGNAL_LABEL[s.type]}</span> <b>{s.headline}</b></div>
      <blockquote className="sig-quote">“{s.quote}”</blockquote>
      <div className="muted small">
        <a href={s.source_url} target="_blank" rel="noreferrer nofollow">{where(s.source_url)}</a> · {dated}
        {s.fresh ? (s.fresh_until ? ` · counts until ${day(s.fresh_until)}` : '') : s.fresh_until ? ' · no longer fresh' : ' · undated, not counted'}
        {s.relevance ? <> · {s.relevance}</> : null}
      </div>
    </li>
  );
}

export function SignalList({ signals }: { signals: Signal[] }) {
  return <ul className="sig-list">{signals.map((s) => <SignalItem key={s.id} s={s} />)}</ul>;
}

/** The account page: its signals, fresh ones first. */
export function AccountSignals({ signals }: { signals: Signal[] }) {
  const fresh = signals.filter((s) => s.fresh).length;
  return (
    <Section title="Why now">
      {signals.length ? <>
        <p className="muted small">{fresh ? `${fresh} fresh buying signal${fresh === 1 ? '' : 's'}: the first email may open with the best one, with its source and date.` : 'No fresh buying signal now; older ones no longer count.'}</p>
        <SignalList signals={signals} />
      </> : <p className="muted">No buying signals found on its pages yet. Research reads them (hiring, funding, a new leader, a launch, a tech change), each with its source and date.</p>}
    </Section>
  );
}

/** The campaign page: which of its accounts have a fresh signal, and the opener each one's first email gets. */
export function CampaignSignals({ campaignId }: { campaignId: string }) {
  const q = useApi<CampaignSignals>(`/v1/campaigns/${campaignId}/signals`);
  const me = useAuthOptional()?.me ?? null;
  if (q.error) return <ErrorBox error={q.error} />;
  const d = q.data;
  if (!d) return null;
  const owner = !me || hasRole(me, 'owner', 'admin');
  return (
    <Section title="Why now" actions={owner ? <ActionButton path="/portal/signal-rechecks" method="PUT" body={{ on: !d.rechecks.on }} label={d.rechecks.on ? 'Turn re-checks off' : 'Turn re-checks on'} /> : null}>
      <p className="muted small">
        {d.with_signal ? `${d.with_signal} of ${d.accounts} accounts have a fresh buying signal.` : d.accounts ? `None of its ${d.accounts} accounts has a fresh buying signal now.` : 'No accounts in this campaign yet.'}
        {' '}{d.rechecks.on ? `Qualified accounts are re-read for new signals every ${d.rechecks.every_days} days while the campaign sends (${d.rechecks.credits_each ?? 1} credit each).` : 'Re-checks are off: signals come only from research runs.'}
      </p>
      {d.data.length ? <ul className="sig-accounts">{d.data.map((a) => (
        <li key={a.company_id}>
          <div><b>{a.company}</b> <span className="muted small">{a.domain}</span></div>
          <p className="small sig-opener">Its first email can open with: “{a.why_now}”</p>
          <SignalList signals={a.signals} />
        </li>
      ))}</ul> : null}
    </Section>
  );
}
